import { useMemo, useState } from 'react';

import { useGetAllEventsWithSearchInfinite } from '~entities/events/api/get-all-events-with-search';

import Select from '~shared/components/select/select';
import { useDebounce } from '~shared/shadcn/expansions/multiple-selector';

import type { AudienceOption } from './multi-class-audience-selector';

interface MultiEventAudienceSelectorProps {
  value: Array<AudienceOption>;
  onChange: (value: Array<AudienceOption>) => void;
  disabled?: boolean;
}

export const MultiEventAudienceSelector = ({
  value,
  onChange,
  disabled,
}: MultiEventAudienceSelectorProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);

  const {
    data,
    isFetching,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useGetAllEventsWithSearchInfinite({
    name: debouncedSearch,
    isArchive: false,
  });

  const options = useMemo(() => {
    const pages = data?.pages ?? [];
    const seen = new Set(value.map((v) => v.value));
    const fromApi: Array<AudienceOption> = [];
    for (const page of pages) {
      const events = page?.data?.events ?? [];
      for (const event of events) {
        if (!seen.has(event._id)) {
          seen.add(event._id);
          fromApi.push({ label: event.title, value: event._id });
        }
      }
    }
    return [...value, ...fromApi];
  }, [data?.pages, value]);

  return (
    <Select
      isClearable
      isMulti
      isDisabled={disabled}
      value={value}
      onChange={(option) => {
        const opt = option as ReadonlyArray<AudienceOption> | null;
        onChange(opt ? [...opt] : []);
      }}
      placeholder='Select events'
      options={options}
      isLoading={isPending || (isFetching && !isFetchingNextPage)}
      isMenuListFetchingMore={isFetchingNextPage}
      onMenuScrollToBottom={() => {
        if (hasNextPage && !isFetchingNextPage) {
          void fetchNextPage();
        }
      }}
      onInputChange={(inputValue, { action }) => {
        if (action === 'input-change') {
          setSearchQuery(inputValue);
        }
      }}
    />
  );
};
